import { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import LeftNav from "../../components/organisms/LeftNav";
import appConfig from "../../lib/appConfig";
import colours from "../../lib/colours";
import {
  Patch,
  Tree,
  nvrToolModes,
  nvrTools,
  pastRemovalOptions,
  uploadTypes,
} from "../../lib/types";
import { removalStages } from "../../lib/workflow";
import DataPanel from "../Other/DataPanel";
import MapNativeVeg from "../Stages/MapNativeVeg";
import PastRemoval from "../Stages/PastRemoval";
import SummaryReport from "../Stages/SummaryReport";
import { IState } from "../../context/featuresReducer";
import {
  useFeaturesContext,
  useNvrToolModeContext,
  useUploadTypeContext,
} from "../../context/ContextProvider";
import { fetchRemovalSummary } from "../../utils/requests";
import { useDebouncedEffect } from "../../utils/useDebouncedEffect";

const Container = styled.div`
  display: flex;
  height: 100%;
`;

const DataPanelContainer = styled.div`
  border-right: 1px solid ${colours.offWhite};
  @media (max-width: ${appConfig.tabletWidth}px) {
    display: block !important;
  }
`;

function RemovalTool() {
  const { pastRemoval, uploadType } = useUploadTypeContext();
  const [dataPanelOpen, setDataPanelOpen] = useState(true);
  const [stage, setStage] = useState<number>(0);
  const [maxEnabledStage, setMaxEnabledStage] = useState<number>(0);

  const state: IState = useFeaturesContext();

  const [pastRemovalPermitPinNumber, setPastRemovalPermitPinNumber] =
    useState<string>("");
  const [pastRemovalArea, setPastRemovalArea] = useState<string>("");
  const pastRemovalProps = {
    pastRemovalPermitPinNumber,
    setPastRemovalPermitPinNumber,
    pastRemovalArea,
    setPastRemovalArea,
  };

  const [locationResults, setLocationResults] = useState<any>(undefined);
  const [removalResults, setRemovalResults] = useState<any>();
  const [fetching, setFetching] = useState(false);
  const [requestError, setRequestError] = useState(false);

  const [pastRemovalAreaValid, setPastRemovalAreaValid] = useState<boolean>();
  const [showPastRemovalStageFooter, setShowPastRemovalStageFooter] =
    useState<boolean>(false);
  const [showMappingStageFooter, setShowMappingStageFooter] =
    useState<boolean>(false);

  const anyUnresolvedMappingIssues = state.treesList.some((x: string) => {
    return (state.features[x] as Tree).benchmarkCm === undefined;
  });

  const {
    nvrToolMode,
    setNvrToolMode,
  }: { nvrToolMode: nvrToolModes; setNvrToolMode: any } =
    useNvrToolModeContext();

  useEffect(() => {
    setNvrToolMode(nvrTools.removal);
  }, [setNvrToolMode]);

  // validate past removal area value
  useEffect(() => {
    const isValid =
      !["", "0"].includes(pastRemovalArea) && Number(pastRemovalArea) > 0;
    setPastRemovalAreaValid(isValid);
  }, [pastRemovalArea, setPastRemovalAreaValid]);

  const pastRemovalNumber = useMemo(() => {
    return pastRemoval === pastRemovalOptions.yes ? Number(pastRemovalArea) : 0;
  }, [pastRemoval, pastRemovalArea]);

  // update workflow progress
  useEffect(() => {
    const validPastRemovalForSimpleUpload =
      pastRemoval === pastRemovalOptions.no ||
      (pastRemoval === pastRemovalOptions.yes && pastRemovalAreaValid);
    const passedStage1 =
      uploadType === uploadTypes.detailed ||
      (uploadType === uploadTypes.simple && validPastRemovalForSimpleUpload);
    if (!passedStage1) {
      setMaxEnabledStage(0);
      setShowPastRemovalStageFooter(false);
      return;
    }
    setShowPastRemovalStageFooter(true);
    const featuresList = state.patchesList
      .concat(state.treesList)
      .concat(state.revegetationAreasList);
    const passedStage2 = !anyUnresolvedMappingIssues && featuresList.length;
    if (!passedStage2) {
      setMaxEnabledStage(1);
      setShowMappingStageFooter(false);
      return;
    }
    setMaxEnabledStage(2);
    setShowMappingStageFooter(true);
  }, [
    pastRemoval,
    uploadType,
    pastRemovalAreaValid,
    anyUnresolvedMappingIssues,
    state,
    setMaxEnabledStage,
    setShowPastRemovalStageFooter,
    setShowMappingStageFooter,
  ]);

  // regenerate summary when state changes
  // debounce the effect so that we avoid excessive amount of requests
  useDebouncedEffect(
    async () => {
      const invalidPastRemoval =
        pastRemoval === pastRemovalOptions.yes && !pastRemovalAreaValid;
      const emptySummary =
        Object.keys(state.features).length === 0 ||
        invalidPastRemoval ||
        anyUnresolvedMappingIssues;
      if (emptySummary) {
        setLocationResults(undefined);
        setRemovalResults(undefined);
        setRequestError(false);
        return;
      }
      setFetching(true);
      const patches = state.patchesList.map((x) => state.features[x] as Patch);
      const trees = state.treesList.map((x) => state.features[x] as Tree);
      await fetchRemovalSummary(patches, trees, pastRemovalNumber, uploadType)
        .then((results: any[]) => {
          setLocationResults(results[0]);
          setRemovalResults(results[1]);
          setRequestError(false);
          setFetching(false);
        })
        .catch((e) => {
          console.error(e);
          setLocationResults(undefined);
          setRemovalResults(undefined);
          setRequestError(true);
          setFetching(false);
        });
    },
    [
      state,
      pastRemoval,
      pastRemovalNumber,
      pastRemovalAreaValid,
      anyUnresolvedMappingIssues,
    ],
    2000
  );

  const renderDataPanelComponent = () => {
    const restProps = {
      close: () => setDataPanelOpen(false),
      setStage,
    };
    switch (stage) {
      case 0:
        return (
          <PastRemoval
            pastRemovalProps={pastRemovalProps}
            showFooter={showPastRemovalStageFooter}
            {...restProps}
          />
        );
      case 1:
        return (
          <MapNativeVeg
            mode={nvrToolMode}
            showFooter={showMappingStageFooter}
            {...restProps}
          />
        );
      case 2:
        return (
          <SummaryReport
            mode={nvrToolMode}
            locationResults={locationResults}
            removalResults={removalResults}
            fetching={fetching}
            requestError={requestError}
            pastRemovalNumber={pastRemovalNumber}
            {...restProps}
          />
        );
    }
  };
  return (
    <Container>
      <LeftNav
        title="Removal Tool"
        stages={removalStages}
        stage={stage}
        maxEnabledStage={maxEnabledStage}
        setStage={setStage}
        dataPanelOpen={dataPanelOpen}
        openDataPanel={() => setDataPanelOpen(true)}
      />
      <DataPanelContainer style={{ display: dataPanelOpen ? "block" : "none" }}>
        <DataPanel>{renderDataPanelComponent()}</DataPanel>
      </DataPanelContainer>
    </Container>
  );
}

export default RemovalTool;
